import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AlumniLayout from "@/components/AlumniLayout";
import { CalendarHeart, MapPin, Image as ImageIcon, Ticket, ArrowRight } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "Date TBD";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const RSVP_STYLES = { attending: "bg-emerald-100 text-emerald-800", maybe: "bg-amber-100 text-amber-800", declined: "bg-red-100 text-red-800" };

export default function AlumniEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/experience/events").then((r) => setEvents(r.data.events)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const isPast = (e) => e.status === "completed" || (e.end && new Date(e.end).getTime() < now) || (e.start && new Date(e.start).getTime() < now);
  const upcoming = [...events].filter((e) => !isPast(e)).sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));
  const past = [...events].filter((e) => isPast(e)).sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0));

  const Card = (e) => (
    <button key={e.id} data-testid={`aevent-card-${e.id}`} onClick={() => navigate(`/experience/events/${e.id}`)} className="text-left bg-card border border-border rounded-lg shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-primary transition-all group">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-primary"><Ticket className="h-3 w-3" /> {e.category}</span>
          {e.photo_count > 0 && <span data-testid={`aevent-gallery-pill-${e.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-primary/10 text-primary"><ImageIcon className="h-3 w-3" /> Gallery</span>}
        </div>
        {e.my_rsvp && e.my_rsvp.status !== "invited" && <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${RSVP_STYLES[e.my_rsvp.status] || ""}`}>You: {e.my_rsvp.status}</span>}
      </div>
      <div className="flex items-start gap-2 text-primary mt-2"><CalendarHeart className="h-5 w-5 shrink-0 mt-0.5" /><span className="font-serif text-xl tracking-tight leading-snug">{e.title}</span></div>
      <div className="text-sm text-muted-foreground mt-2">{fmtDate(e.start)}</div>
      {(e.location?.venue || e.location?.city) && <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-3.5 w-3.5" />{[e.location?.venue, e.location?.city].filter(Boolean).join(", ")}</div>}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><ImageIcon className="h-3.5 w-3.5" /> {e.photo_count || 0} photo{e.photo_count === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-1 text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">View &amp; RSVP <ArrowRight className="h-4 w-4" /></span>
      </div>
    </button>
  );

  return (
    <AlumniLayout wide>
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Reunion &amp; Class Events</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1">Events &amp; Photo Galleries</h1>
      </div>
      {loading && <div className="text-muted-foreground">Loading…</div>}
      {!loading && events.length === 0 && <div className="text-muted-foreground" data-testid="aevents-empty">No published events yet — check back soon.</div>}

      {upcoming.length > 0 && (
        <section data-testid="aevents-upcoming" className="mb-10">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Upcoming</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{upcoming.map(Card)}</div>
        </section>
      )}

      {past.length > 0 && (
        <section data-testid="aevents-past">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Past Events &amp; Photos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{past.map(Card)}</div>
        </section>
      )}
    </AlumniLayout>
  );
}
